import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Layout from '../../components/layout/Layout';
import physicalStockService from '../../services/physicalStockService';
import axiosInstance from '../../utils/axios';

const ViewPhysicalStockEntry = () => {
  const [loading, setLoading] = useState(true);
  const [stocks, setStocks] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    return d.toISOString().split('T')[0];
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const toDate = (value) => {
    if (!value) return null;
    if (Array.isArray(value)) {
      const [y, m, d, hh = 0, mm = 0, ss = 0] = value;
      return new Date(y, m - 1, d, hh, mm, ss);
    }
    const dt = new Date(value);
    return isNaN(dt.getTime()) ? null : dt;
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [stockRes, locationRes] = await Promise.all([
          physicalStockService.getAll(),
          axiosInstance.get('/locations', { params: { page: 0, size: 1000, sort: 'name', direction: 'ASC' } })
        ]);
        console.log('Physical stock GET /physical-stock raw response', stockRes);
        const stockList = Array.isArray(stockRes) ? stockRes : (stockRes?.content || []);
        const locList = locationRes.data?.content || locationRes.data || [];
        console.log('Resolved physical stock list size', stockList?.length || 0);
        console.log('Resolved locations size', locList?.length || 0);
        setStocks(stockList);
        setLocations(locList);
      } catch (e) {
        console.error('Failed to load physical stock entries', e);
        setStocks([]);
        setLocations([]);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const filteredStocks = useMemo(() => {
    const s = new Date(startDate);
    const e = new Date(endDate);
    e.setHours(23, 59, 59, 999);
    return (stocks || []).filter(ps => {
      const ts = ps.stockCheckDate ? toDate(ps.stockCheckDate) : null;
      const withinDate = ts ? ts >= s && ts <= e : true;
      const withinLocation = selectedLocation === '' ||
        (selectedLocation === 'warehouse' && !ps.locationId) ||
        (ps.locationId && ps.locationId.toString() === selectedLocation.toString());
      return withinDate && withinLocation;
    });
  }, [stocks, startDate, endDate, selectedLocation]);

  const totalEntries = filteredStocks.length;
  const totals = useMemo(() => {
    let pos = 0;
    let neg = 0;
    let net = 0;
    filteredStocks.forEach(m => {
      const diffs = (m.details || []).reduce((sum, d) => sum + Number(d?.difference || 0), 0);
      if (diffs > 0) pos += diffs;
      if (diffs < 0) neg += diffs;
      net += diffs;
    });
    return { pos, neg, net };
  }, [filteredStocks]);

  const handleApplyFilters = () => {
    setPage(0);
  };

  const handleViewEntry = async (entry) => {
    if (!entry) return;
    if (!entry.details || entry.details.length === 0) {
      try {
        const data = await physicalStockService.getById(entry.id);
        setSelectedEntry(data);
      } catch {
        setSelectedEntry(entry);
      }
    } else {
      setSelectedEntry(entry);
    }
    setDialogOpen(true);
  };

  const paginated = filteredStocks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = toDate(iso);
    if (!d) return { date: '', time: '' };
    const date = d.toLocaleDateString();
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return { date, time };
  };

  const getLocationName = (locId) => {
    if (!locId) return 'Warehouse';
    const loc = locations.find(l => l.id === locId);
    return loc ? loc.name : String(locId);
  };

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">View Physical Stock Entry</Typography>
        </Box>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="location-select-label" shrink={true}>Location</InputLabel>
                  <Select
                    labelId="location-select-label"
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>All Locations</em>
                    </MenuItem>
                   
                    {locations.map(loc => (
                      <MenuItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button fullWidth variant="contained" onClick={handleApplyFilters} disabled={loading} sx={{ height: '56px' }}>
                  Apply Filter
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <Typography>Loading...</Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" variant="body2">Total Entries</Typography>
                    <Typography variant="h4" color="primary.main">{totalEntries}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" variant="body2">Positive Adjustments</Typography>
                    <Typography variant="h4" color="success.main">{Number(totals.pos.toFixed(2))}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" variant="body2">Negative Adjustments</Typography>
                    <Typography variant="h4" color="error.main">{Number(totals.neg.toFixed(2))}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" variant="body2">Net Difference</Typography>
                    <Typography variant="h4">{Number(totals.net.toFixed(2))}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Physical Stock Entries</Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Reference No</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Time</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Notes</TableCell>
                        <TableCell>Reasons</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Net Difference</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginated.length > 0 ? paginated.map(entry => {
                        const dt = formatDate(entry.stockCheckDate);
                        const net = (entry.details || []).reduce((sum, d) => sum + Number(d?.difference || 0), 0);
                        const reasons = Array.from(new Set((entry.details || [])
                          .map(d => (d?.reason || '').trim())
                          .filter(r => r))).join(', ');
                        return (
                          <TableRow key={entry.id}>
                            <TableCell>{entry.referenceNumber}</TableCell>
                            <TableCell>{dt.date}</TableCell>
                            <TableCell>{dt.time}</TableCell>
                            <TableCell>{getLocationName(entry.locationId)}</TableCell>
                            <TableCell>{entry.notes || '-'}</TableCell>
                            <TableCell>{reasons || '-'}</TableCell>
                            <TableCell>{entry.status || 'COMPLETED'}</TableCell>
                            <TableCell align="right" style={{ color: net < 0 ? '#d32f2f' : net > 0 ? '#2e7d32' : 'inherit', fontWeight: net !== 0 ? 600 : 400 }}>
                              {net > 0 ? `+${Number(net.toFixed(2))}` : Number(net.toFixed(2))}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton size="small" color="primary" onClick={() => handleViewEntry(entry)}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      }) : (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            <Typography variant="body2" color="text.secondary">No entries found for the selected filters</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={filteredStocks.length}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[10]}
                  sx={{ borderTop: '1px solid rgba(224, 224, 224, 1)' }}
                />
              </CardContent>
            </Card>
          </>
        )}

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Physical Stock Details</DialogTitle>
          <DialogContent dividers>
            {selectedEntry && (
              <Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Reference No</Typography>
                    <Typography variant="subtitle1">{selectedEntry.referenceNumber}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Location</Typography>
                    <Typography variant="subtitle1">{getLocationName(selectedEntry.locationId)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Date</Typography>
                    <Typography variant="subtitle1">{formatDate(selectedEntry.stockCheckDate).date} {formatDate(selectedEntry.stockCheckDate).time}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Created By</Typography>
                    <Typography variant="subtitle1">{selectedEntry.createdByName || '-'}</Typography>
                  </Grid>
                </Grid>
                <Divider sx={{ mb: 2 }} />
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">System Stock</TableCell>
                        <TableCell align="right">Physical Stock</TableCell>
                        <TableCell align="right">Difference</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(selectedEntry.details || []).map(d => {
                        const diff = Number(d?.difference || 0);
                        return (
                          <TableRow key={d.id || `${selectedEntry.id}-${d.productId}`}>
                            <TableCell>{d.productName}</TableCell>
                            <TableCell align="right">{Number(d.systemStock ?? 0)}</TableCell>
                            <TableCell align="right">{Number(d.physicalStock ?? 0)}</TableCell>
                            <TableCell align="right" style={{ color: diff < 0 ? '#d32f2f' : diff > 0 ? '#2e7d32' : 'inherit', fontWeight: diff !== 0 ? 600 : 400 }}>
                              {diff > 0 ? `+${Number(diff.toFixed(2))}` : Number(diff.toFixed(2))}
                            </TableCell>
                            <TableCell>{d.reason || '-'}</TableCell>
                          </TableRow>
                        );
                      })}
                      {(!selectedEntry.details || selectedEntry.details.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">No details found</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default ViewPhysicalStockEntry;
